import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import { authHeaders, getEditPassword } from '../../services/auth';
import PasswordGate from '../../components/PasswordGate';

const TYPE_CONFIG = {
  new_trial:             { label: 'New Trial',      color: '#2563eb', bg: '#eff6ff' },
  phase_change:          { label: 'Phase Advance',  color: '#059669', bg: '#ecfdf5' },
  status_change:         { label: 'Status Change',  color: '#7c3aed', bg: '#f5f3ff' },
  new_program_candidate: { label: 'New Program',    color: '#d97706', bg: '#fffbeb' },
  page_diff:             { label: 'Page Changed',   color: '#dc2626', bg: '#fef2f2' },
};

// ---------------------------------------------------------------------------
// Monitored URLs panel
// ---------------------------------------------------------------------------

function MonitoredUrlsPanel({ editMode }) {
  const [urls,      setUrls]      = useState([]);
  const [companies, setCompanies] = useState([]);
  const [adding,    setAdding]    = useState(false);
  const [form,      setForm]      = useState({ company_id: '', url: '', label: '' });
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  const loadUrls = useCallback(() => {
    axios.get(`${API_BASE_URL}/pipeline/urls`)
      .then(res => setUrls(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadUrls();
    axios.get(`${API_BASE_URL}/companies`)
      .then(res => setCompanies(res.data.companies || res.data || []))
      .catch(() => {});
  }, [loadUrls]);

  async function handleAdd(e) {
    e.preventDefault();
    setError('');
    if (!form.company_id || !form.url.trim()) { setError('Company and URL are required.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/pipeline/urls`, {
        company_id: Number(form.company_id),
        url: form.url.trim(),
        label: form.label.trim() || undefined,
      }, { headers: authHeaders() });
      setForm({ company_id: '', url: '', label: '' });
      setAdding(false);
      loadUrls();
    } catch {
      setError('Failed to add URL.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Remove this monitored URL?')) return;
    await axios.delete(`${API_BASE_URL}/pipeline/urls/${id}`, { headers: authHeaders() });
    setUrls(prev => prev.filter(u => u.id !== id));
  }

  function formatChecked(ts) {
    if (!ts) return 'Never';
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // Find company id for a url row so we can link to /pipeline/companies/:id
  function companyId(row) {
    return row.company_id;
  }

  return (
    <div className="card" style={{ marginTop: 28, padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
          Monitored Pipeline URLs
          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>
            ({urls.length})
          </span>
        </div>
        {editMode && !adding && (
          <button
            onClick={() => setAdding(true)}
            className="btn-secondary"
            style={{ fontSize: 12, padding: '4px 12px' }}
          >
            + Add URL
          </button>
        )}
      </div>

      {urls.length === 0 && !adding && (
        <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '12px 0' }}>
          No URLs configured. Add a company pipeline page to enable snapshot monitoring.
        </div>
      )}

      {urls.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
              <th style={{ textAlign: 'left', padding: '4px 8px 6px 0', color: '#64748b', fontWeight: 600 }}>Company</th>
              <th style={{ textAlign: 'left', padding: '4px 8px 6px', color: '#64748b', fontWeight: 600 }}>Label / URL</th>
              <th style={{ textAlign: 'left', padding: '4px 0 6px 8px', color: '#64748b', fontWeight: 600 }}>Last checked</th>
              {editMode && <th />}
            </tr>
          </thead>
          <tbody>
            {urls.map(u => (
              <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '7px 8px 7px 0', verticalAlign: 'top' }}>
                  <Link
                    to={`/pipeline/companies/${companyId(u)}`}
                    style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
                  >
                    {u.company_name}
                  </Link>
                </td>
                <td style={{ padding: '7px 8px', verticalAlign: 'top' }}>
                  {u.label && (
                    <div style={{ fontWeight: 500, color: '#1e293b', marginBottom: 2 }}>{u.label}</div>
                  )}
                  <a
                    href={u.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#64748b', wordBreak: 'break-all' }}
                  >
                    {u.url}
                  </a>
                </td>
                <td style={{ padding: '7px 0 7px 8px', verticalAlign: 'top', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                  {formatChecked(u.last_checked_at)}
                </td>
                {editMode && (
                  <td style={{ padding: '7px 0 7px 8px', verticalAlign: 'top' }}>
                    <button
                      onClick={() => handleDelete(u.id)}
                      style={{
                        fontSize: 11, padding: '2px 8px', border: '1px solid #fca5a5',
                        borderRadius: 4, background: '#fff', color: '#dc2626', cursor: 'pointer',
                      }}
                    >
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {adding && (
        <form onSubmit={handleAdd} style={{ marginTop: urls.length ? 14 : 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select
              value={form.company_id}
              onChange={e => setForm(f => ({ ...f, company_id: e.target.value }))}
              style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, minWidth: 160 }}
            >
              <option value="">Select company…</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <input
              type="url"
              placeholder="https://…"
              value={form.url}
              onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
              style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, flex: 1, minWidth: 200 }}
            />
            <input
              type="text"
              placeholder="Label (optional)"
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, minWidth: 140 }}
            />
          </div>
          {error && <div style={{ fontSize: 12, color: '#dc2626' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" disabled={saving} className="btn-primary" style={{ fontSize: 12, padding: '5px 14px' }}>
              {saving ? 'Saving…' : 'Add'}
            </button>
            <button type="button" onClick={() => { setAdding(false); setError(''); }} className="btn-secondary" style={{ fontSize: 12, padding: '5px 14px' }}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function ChangesPage() {
  const [changes,    setChanges]    = useState([]);
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [filter,          setFilter]          = useState('unacknowledged'); // 'all' | 'unacknowledged'
  const [editMode,        setEditMode]        = useState(() => !!getEditPassword());
  const [showUnlockGate,  setShowUnlockGate]  = useState(false);
  const [ackingAll,       setAckingAll]       = useState(false);

  const unreadCount = changes.filter(c => !c.acknowledged).length;

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 100 };
    if (filter === 'unacknowledged') params.unacknowledged = '1';
    axios.get(`${API_BASE_URL}/pipeline/changes`, { params })
      .then(res => { setChanges(res.data.changes || []); setTotal(res.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function acknowledge(id) {
    await axios.post(`${API_BASE_URL}/pipeline/changes/${id}/acknowledge`, {}, { headers: authHeaders() });
    setChanges(prev => prev.map(c => c.id === id ? { ...c, acknowledged: true } : c));
  }

  async function acknowledgeAll() {
    setAckingAll(true);
    try {
      await axios.post(`${API_BASE_URL}/pipeline/changes/acknowledge-all`, {}, { headers: authHeaders() });
      load();
    } finally {
      setAckingAll(false);
    }
  }

  function formatDate(ts) {
    if (!ts) return '—';
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const displayed = filter === 'unacknowledged'
    ? changes.filter(c => !c.acknowledged)
    : changes;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Pipeline Changes</h1>
        <p className="page-subtitle">
          {loading ? 'Loading…' : `${total} total change${total !== 1 ? 's' : ''}`}
          {unreadCount > 0 && (
            <span style={{ marginLeft: 10, background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 12, fontWeight: 600 }}>
              {unreadCount} new
            </span>
          )}
        </p>
        {editMode ? (
          <span style={{ fontSize: 12, color: '#059669', fontWeight: 500 }}>Edit mode on</span>
        ) : showUnlockGate ? (
          <PasswordGate onUnlocked={() => { setEditMode(true); setShowUnlockGate(false); }} />
        ) : (
          <button className="btn-secondary" style={{ marginTop: 4 }} onClick={() => setShowUnlockGate(true)}>
            Edit
          </button>
        )}
      </div>

      <div className="page-body">
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' }}>
          <div className="filter-bar" style={{ margin: 0, flex: 1 }}>
            <select value={filter} onChange={e => setFilter(e.target.value)}>
              <option value="unacknowledged">Unacknowledged only</option>
              <option value="all">All changes</option>
            </select>
          </div>
          {editMode && unreadCount > 0 && (
            <button
              onClick={acknowledgeAll}
              disabled={ackingAll}
              className="btn-secondary"
              style={{ whiteSpace: 'nowrap' }}
            >
              {ackingAll ? 'Marking…' : 'Mark all read'}
            </button>
          )}
        </div>

        {loading && <div className="loading-state">Loading changes…</div>}

        {!loading && displayed.length === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: '40px 24px', color: '#94a3b8' }}>
            {filter === 'unacknowledged' ? 'No new changes — you\'re up to date.' : 'No changes recorded yet.'}
            <p style={{ fontSize: 13, marginTop: 8 }}>
              Run <code>pipeline_monitor.py</code> on the server to check for updates.
            </p>
          </div>
        )}

        {!loading && displayed.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {displayed.map(change => {
              const cfg = TYPE_CONFIG[change.change_type] || { label: change.change_type, color: '#64748b', bg: '#f8fafc' };
              const detail = typeof change.detail === 'string' ? JSON.parse(change.detail || '{}') : (change.detail || {});
              return (
                <div
                  key={change.id}
                  className="card"
                  style={{
                    padding: '14px 18px',
                    opacity: change.acknowledged ? 0.55 : 1,
                    borderLeft: `3px solid ${cfg.color}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 8,
                          background: cfg.bg, color: cfg.color,
                        }}>
                          {cfg.label}
                        </span>
                        {change.company_name && change.company_id ? (
                          <Link
                            to={`/pipeline/companies/${change.company_id}`}
                            style={{ fontSize: 12, color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
                          >
                            {change.company_name}
                          </Link>
                        ) : change.company_name && (
                          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{change.company_name}</span>
                        )}
                        <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>
                          {formatDate(change.detected_at)}
                        </span>
                      </div>

                      <div style={{ fontSize: 14, color: '#1e293b', fontWeight: 500 }}>
                        {change.description}
                      </div>

                      {detail.added_samples?.length > 0 && (
                        <div style={{ marginTop: 6, fontSize: 12, color: '#16a34a' }}>
                          + {detail.added_samples.slice(0, 2).join(' | ')}
                        </div>
                      )}
                      {detail.removed_samples?.length > 0 && (
                        <div style={{ marginTop: 2, fontSize: 12, color: '#dc2626' }}>
                          – {detail.removed_samples.slice(0, 2).join(' | ')}
                        </div>
                      )}
                    </div>

                    {editMode && !change.acknowledged && (
                      <button
                        onClick={() => acknowledge(change.id)}
                        style={{
                          flexShrink: 0, fontSize: 11, padding: '3px 10px',
                          border: '1px solid #d1d5db', borderRadius: 5,
                          background: '#f8fafc', cursor: 'pointer', color: '#374151',
                        }}
                      >
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <MonitoredUrlsPanel editMode={editMode} />

        <div className="card" style={{ marginTop: 16, padding: '16px 20px', background: '#f8fafc' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>About monitoring</div>
          <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.7 }}>
            <p style={{ margin: '0 0 4px' }}>
              <strong>CT.gov tier</strong> — checks for new trials, phase advances, and status changes across all linked programs.
            </p>
            <p style={{ margin: '0 0 4px' }}>
              <strong>Page snapshot tier</strong> — diffs company pipeline pages against a stored baseline. Add URLs above to enable.
            </p>
            <p style={{ margin: 0 }}>
              Run the monitor manually or set up a weekly cron job on the server:<br />
              <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: 3 }}>
                0 6 * * 1 /opt/miniconda/envs/myrdkit/bin/python3 /opt/chemdb/pipeline_monitor.py {'>>'}  /var/log/pipeline_monitor.log 2{'>&'}1
              </code>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChangesPage;
